// economics.js — PURE unit-economics math for the what-if simulator (Economics
// screen). No imports, no I/O, no dates — every function takes plain numbers and
// returns plain objects so it's trivially unit-testable and reusable.
//
// Model (all money in INR/month unless noted):
//   Revenue  = revenueOPD + revenueIPD
//     revenueOPD = hospitals × subscription(model) + (hybrid only) consults × pricePerConsult
//     revenueIPD = IPD admissions × price/admission   (or bed-days × price/bed-day)
//   COGS     = aiCostOPD + aiCostIPD + infra(hospitals)   [infra is a tiered step fn]
//     aiCostOPD  = consults × aiCostPerConsult   (DERIVED from STT/TTS/LLM/messaging sub-levers,
//                                                  or a single blended override)
//     aiCostIPD  = IPD bed-days × aiCostPerBedDay
//     mobileData = mobileDataPerSeatINR × doctorsPerHospital × hospitals — a recurring
//                  per-hospital serving cost (SIM/data per staff seat), inside COGS
//   Gross    = Revenue − COGS
//   Opex     = staff + misc + CAC × newHospitalsAcquired  (acquisition expensed)
//   EBITDA   = Gross − Opex
//   Hardware = kiosks/hospital × per-kiosk cost. Two modes, set by kioskSellPriceINR:
//     • SOLD (sellPrice > 0): we buy the tablet (~kioskCostINR) and re-sell it to the
//       hospital at a markup. Each NEW hospital's landing month books hardware
//       REVENUE = kiosks × sellPrice and hardware COGS = kiosks × kioskCost — a
//       product sale, NOT our capex — so kiosk capex and amortization are ZERO for
//       sold hardware. The hardware margin = kiosks × (sellPrice − cost) flows into
//       EBITDA / NPV / IRR naturally through revenue − COGS, exactly like any sale.
//     • GIVEN (sellPrice = 0): we give the tablet free — it stays OUR capex, shown as
//       straight-line amortization below EBITDA (the "DA"), never inside EBITDA, and
//       the cash leaves as capex in each new hospital's landing month (legacy behavior).
// Growth    : each month acquire `hospitalsAddedPerMonth`, churn `churnMonthlyPct`%.
// LTV       : monthly gross margin per hospital ÷ monthly churn rate.
// Payback   : (CAC + kiosk hardware) ÷ monthly gross margin per hospital.

export const DEFAULT_LEVERS = {
  // ---- Growth ---------------------------------------------------------------
  startingHospitals: 4,
  hospitalsAddedPerMonth: 2,
  churnMonthlyPct: 2,           // % of paying hospitals that cancel each month

  // ---- OPD usage ------------------------------------------------------------
  consultsPerHospitalPerDay: 60,
  workingDaysPerMonth: 26,

  // ---- AI cost per consult (DERIVED from real per-unit analysis) ------------
  // Blended cost = STT minutes × STT ₹/min + TTS + LLM + messaging. Defaults are
  // the observed per-unit rates; the screen shows the computed total feeding the P&L.
  sttMinutesPerConsult: 12.5,   // speech-to-text minutes/consult: 2.5 kiosk + 10 consult
  sttPerMinuteINR: 0.55,        // blend of Sarvam ₹0.50 and Deepgram ₹0.66 per minute
  ttsPerConsultINR: 0,          // text-to-speech: Supertonic runs on-device (was ₹3 on Sarvam Bulbul)
  llmPerConsultINR: 1.7,        // LLM/inference: Gemma-on-Cerebras intake + drafting legs
  messagingPerPatientINR: 0.7,  // 3–4 WhatsApp messages/patient at ₹0.20 each (one patient per consult)
  // Override: when on, ignore the sub-levers and use one blended number instead.
  // The "AI cost from live data" button fills THIS override (and turns it on).
  aiCostOverrideEnabled: false,
  aiCostOverrideINR: 6,

  // ---- Subscription / pricing model -----------------------------------------
  pricingModel: 'hybrid',       // 'flat' | 'per-seat' | 'hybrid' | 'tiered'
  subscriptionPerHospitalINR: 8000, // flat platform fee per hospital per month (flat & hybrid)
  pricePerConsultINR: 12,       // usage fee charged per consult (hybrid only)
  doctorsPerHospital: 8,        // per-seat model: doctors billed per hospital
  pricePerSeatINR: 1500,        // per-seat model: ₹ per doctor per month

  // ---- Tiered slabs + base (the 'tiered' model) -----------------------------
  // A monthly base platform fee plus graduated per-consult usage priced in slabs
  // (marginal, like income-tax brackets): cheaper the more a hospital runs. A
  // one-time setup fee is booked as revenue in the month each NEW hospital lands.
  tierBaseFeeINR: 3000,         // fixed platform fee / hospital / month
  slab1UpToConsults: 1000,      // first slab: consults 0 .. this, priced at slab1INR
  slab1INR: 15,                 // ₹ / consult inside slab 1
  slab2UpToConsults: 2500,      // second slab: consults slab1UpTo .. this, at slab2INR
  slab2INR: 10,                 // ₹ / consult inside slab 2
  slab3INR: 6,                  // ₹ / consult beyond slab 2 (the marginal floor)
  setupFeePerHospitalINR: 50000, // one-time onboarding fee booked when a hospital lands

  // ---- IPD (in-patient department) module -----------------------------------
  ipdHospitalPct: 50,           // % of hospitals that also run IPD
  ipdAdmissionsPerHospitalPerMonth: 40,
  ipdAvgLengthOfStayDays: 3,
  ipdPricingMode: 'per-admission', // 'per-admission' | 'per-bed-day'
  ipdPricePerAdmissionINR: 150,
  ipdPricePerBedDayINR: 50,
  ipdAiCostPerBedDayINR: 2,     // ward-round STT + due-engine inference per bed-day

  // ---- Acquisition, hardware & team -----------------------------------------
  cacPerHospitalINR: 60000,     // sales visits + demos + onboarding + training + first-month support
  kioskCostINR: 25000,          // per-kiosk one-time cost to US (tablet + stand + SIM)
  kioskSellPriceINR: 70000,     // ₹ we CHARGE the hospital per kiosk (0 = we give hardware free).
                                // >0 → hardware is sold at a margin (revenue+COGS, no capex/amort).
  kiosksPerHospital: 1,         // kiosks handed to each hospital
  hardwareAmortizationMonths: 24,    // spread hardware over this many months (the "DA" in EBITDA) — GIVEN mode only
  mobileDataPerSeatINR: 350,    // monthly SIM/data allowance per staff seat → × doctorsPerHospital = per-hospital serving cost
  staffCostMonthlyINR: 350000,  // team payroll
  miscOpexMonthlyINR: 60000,    // tools, travel, compliance

  // ---- Infra — tiered "go server" step function -----------------------------
  baseInfraMonthlyINR: 30000,   // shared baseline (current real footprint: EC2 + Medplum ECS/RDS)
  hospitalsPerServer: 10,       // capacity of the baseline AND of each added server (an ASSUMPTION)
  serverStepMonthlyINR: 22000,  // each extra dedicated server adds this

  // ---- Finance (discounted cash-flow) ---------------------------------------
  discountRateAnnualPct: 18,    // annual discount rate for NPV; monthly = (1+r)^(1/12)-1

  months: 12,
};

// Lever fields that are NOT plain numbers — the screen's sanitizer must pass these
// through untouched (numeric sanitize would drop them).
export const STRING_LEVERS = ['pricingModel', 'ipdPricingMode'];
export const BOOLEAN_LEVERS = ['aiCostOverrideEnabled'];

/**
 * AI-cost-per-consult breakdown from the sub-levers. Pure. All four legs sum to
 * the derived blended cost; the override (when enabled) is a single flat number.
 */
export function aiCostBreakdown(L) {
  const stt = Math.max(0, (L.sttMinutesPerConsult || 0) * (L.sttPerMinuteINR || 0));
  const tts = Math.max(0, L.ttsPerConsultINR || 0);
  const llm = Math.max(0, L.llmPerConsultINR || 0);
  const messaging = Math.max(0, L.messagingPerPatientINR || 0); // one patient per consult
  const derivedTotal = stt + tts + llm + messaging;
  return { stt, tts, llm, messaging, derivedTotal };
}

/** Effective AI cost per consult actually used in the P&L (override or derived). */
export function effectiveAiCostPerConsult(L) {
  if (L.aiCostOverrideEnabled) return Math.max(0, L.aiCostOverrideINR || 0);
  return aiCostBreakdown(L).derivedTotal;
}

/** Platform subscription revenue ONE hospital contributes, per the pricing model. */
export function subscriptionRevenuePerHospital(L) {
  if (L.pricingModel === 'per-seat') {
    return Math.max(0, (L.doctorsPerHospital || 0) * (L.pricePerSeatINR || 0));
  }
  // 'flat' and 'hybrid' both charge the flat per-hospital platform fee.
  return Math.max(0, L.subscriptionPerHospitalINR || 0);
}

/** Only the hybrid model adds a per-consult usage fee on top of the subscription. */
export function usesPerConsultRevenue(L) {
  return L.pricingModel === 'hybrid';
}

/**
 * Tiered model: recurring revenue ONE hospital contributes for `consultsPerMonth`
 * consults = a fixed monthly base fee + graduated per-consult usage priced in
 * slabs. The slabs are MARGINAL, exactly like income-tax brackets: the first
 * `slab1UpToConsults` consults each cost `slab1INR`; the next block up to
 * `slab2UpToConsults` each cost `slab2INR`; every consult beyond `slab2UpToConsults`
 * costs `slab3INR`. So a hospital that runs a lot pays a lower AVERAGE ₹/consult,
 * while the base fee guarantees us a floor on every hospital. Pure. (The one-time
 * setup fee is NOT here — it's booked once, in monthPnl, when a hospital lands.)
 */
export function tieredMonthlyRevenuePerHospital(consultsPerMonth, L) {
  const c = Math.max(0, consultsPerMonth || 0);
  const base = Math.max(0, L.tierBaseFeeINR || 0);
  const s1Cap = Math.max(0, L.slab1UpToConsults || 0);
  const s2Cap = Math.max(s1Cap, L.slab2UpToConsults || 0); // never below slab-1 cap
  const r1 = Math.max(0, L.slab1INR || 0);
  const r2 = Math.max(0, L.slab2INR || 0);
  const r3 = Math.max(0, L.slab3INR || 0);
  const inSlab1 = Math.min(c, s1Cap);                       // consults billed at r1
  const inSlab2 = Math.min(Math.max(0, c - s1Cap), s2Cap - s1Cap); // billed at r2
  const inSlab3 = Math.max(0, c - s2Cap);                   // billed at r3
  const usage = inSlab1 * r1 + inSlab2 * r2 + inSlab3 * r3;
  return base + usage;
}

/** The tiered model prices recurring revenue by base + graduated slabs. */
export function usesTieredRevenue(L) {
  return L.pricingModel === 'tiered';
}

/**
 * IPD economics for a given hospital count. `ipdHospitalPct`% of hospitals run
 * IPD; each does `admissions/month`, each admission stays `avgLOS` days. Revenue
 * is per-admission OR per-bed-day (toggle). Pure.
 */
export function ipdMonthly(hospitals, L) {
  const ipdHospitals = Math.max(0, hospitals) * (Math.max(0, L.ipdHospitalPct || 0) / 100);
  const admissions = ipdHospitals * Math.max(0, L.ipdAdmissionsPerHospitalPerMonth || 0);
  const bedDays = admissions * Math.max(0, L.ipdAvgLengthOfStayDays || 0);
  const revenue = L.ipdPricingMode === 'per-bed-day'
    ? bedDays * Math.max(0, L.ipdPricePerBedDayINR || 0)
    : admissions * Math.max(0, L.ipdPricePerAdmissionINR || 0);
  const aiCost = bedDays * Math.max(0, L.ipdAiCostPerBedDayINR || 0);
  return { ipdHospitals, admissions, bedDays, revenue, aiCost };
}

/** Kiosk hardware handed to one hospital = kiosks × per-kiosk cost (our cost). */
export function hardwarePerHospital(L) {
  return Math.max(0, (L.kiosksPerHospital || 0) * (L.kioskCostINR || 0));
}

/** True when we SELL the kiosk to the hospital (kioskSellPriceINR > 0) vs give it free. */
export function hardwareSold(L) {
  return Math.max(0, L.kioskSellPriceINR || 0) > 0;
}

/**
 * Hardware gross margin ONE hospital contributes when hardware is sold =
 * kiosks × (sellPrice − cost). Zero in "given free" mode (sellPrice 0). Pure.
 * This is a one-time product-sale margin, booked once when the hospital lands —
 * it is NOT recurring, so it stays out of LTV / margin-per-hospital-per-month.
 */
export function hardwareMarginPerHospital(L) {
  if (!hardwareSold(L)) return 0;
  const kiosks = Math.max(0, L.kiosksPerHospital || 0);
  return kiosks * (Math.max(0, L.kioskSellPriceINR || 0) - Math.max(0, L.kioskCostINR || 0));
}

/** Recurring monthly mobile-data serving cost for ONE hospital = ₹/seat × doctors. */
export function mobileDataPerHospital(L) {
  return Math.max(0, L.mobileDataPerSeatINR || 0) * Math.max(0, L.doctorsPerHospital || 0);
}

/**
 * Tiered infra step function: the shared baseline covers the first
 * `hospitalsPerServer` hospitals; every further block of `hospitalsPerServer`
 * (or part of one) adds one dedicated server at `serverStepMonthlyINR`.
 * infra(0..cap) = base; infra(cap+1..2cap) = base + step; etc.
 */
export function infraCostForHospitals(hospitals, { baseInfraMonthlyINR, hospitalsPerServer, serverStepMonthlyINR }) {
  const h = Math.max(0, hospitals);
  const cap = Math.max(1, hospitalsPerServer);
  const extraServers = Math.max(0, Math.ceil((h - cap) / cap));
  return baseInfraMonthlyINR + extraServers * serverStepMonthlyINR;
}

/** One month's P&L at a given hospital count + acquisitions. Pure. */
export function monthPnl(hospitals, newHospitals, L) {
  const consults = hospitals * L.consultsPerHospitalPerDay * L.workingDaysPerMonth;
  const aiPerConsult = effectiveAiCostPerConsult(L);

  // OPD revenue, per pricing model:
  //  • flat / per-seat: platform subscription only.
  //  • hybrid: subscription + a per-consult usage fee.
  //  • tiered: a base fee + graduated slab usage (split here into a "subscription"
  //    base line and a "usage" line for the P&L), plus a one-time setup fee booked
  //    in the month each NEW hospital lands.
  let revenueSubscription;
  let revenueUsage;
  let revenueSetup = 0;
  if (usesTieredRevenue(L)) {
    const consultsPerHospital = L.consultsPerHospitalPerDay * L.workingDaysPerMonth;
    const perHospital = tieredMonthlyRevenuePerHospital(consultsPerHospital, L);
    revenueSubscription = hospitals * Math.max(0, L.tierBaseFeeINR || 0);      // the base line
    revenueUsage = hospitals * (perHospital - Math.max(0, L.tierBaseFeeINR || 0)); // graduated usage
    revenueSetup = Math.max(0, newHospitals) * Math.max(0, L.setupFeePerHospitalINR || 0);
  } else {
    revenueSubscription = hospitals * subscriptionRevenuePerHospital(L);
    revenueUsage = usesPerConsultRevenue(L) ? consults * L.pricePerConsultINR : 0;
  }
  const revenueOPD = revenueSubscription + revenueUsage + revenueSetup;
  const aiCostOPD = consults * aiPerConsult;

  // IPD revenue + AI cost (separate labeled lines so OPD vs IPD contribution shows).
  const ipd = ipdMonthly(hospitals, L);
  const revenueIPD = ipd.revenue;
  const aiCostIPD = ipd.aiCost;

  // Hardware sold-at-a-margin vs given-free. When sold (kioskSellPriceINR > 0), each
  // NEW hospital's landing month books a product sale: hardware REVENUE (kiosks ×
  // sellPrice) and hardware COGS (kiosks × kioskCost). When given, no hardware
  // revenue/COGS — it's our capex (handled below EBITDA + in cash flow).
  const hwPerHospital = hardwarePerHospital(L);       // our per-hospital kiosk COST
  const sold = hardwareSold(L);
  const newH = Math.max(0, newHospitals);
  const hardwareRevenue = sold ? newH * Math.max(0, L.kiosksPerHospital || 0) * Math.max(0, L.kioskSellPriceINR || 0) : 0;
  const hardwareCogs = sold ? newH * hwPerHospital : 0;

  // Mobile-data allowance: a recurring per-hospital serving cost (SIM/data per staff
  // seat), inside COGS — it scales with the installed base, like AI cost.
  const mobileDataCost = hospitals * mobileDataPerHospital(L);

  const revenue = revenueOPD + revenueIPD + hardwareRevenue;
  const aiCost = aiCostOPD + aiCostIPD; // total AI COGS (kept for charts/exports)
  const infra = infraCostForHospitals(hospitals, L);
  const cogs = aiCost + infra + mobileDataCost + hardwareCogs;
  const grossMargin = revenue - cogs;
  const acquisition = newHospitals * L.cacPerHospitalINR;
  const opex = L.staffCostMonthlyINR + L.miscOpexMonthlyINR + acquisition;
  const ebitda = grossMargin - opex; // sold-hardware margin already flows in via revenue − COGS

  // GIVEN-free hardware only: capex that does NOT reduce EBITDA (the "DA" is exactly
  // this), shown separately as straight-line amortization over the fleet. When
  // hardware is SOLD its cost is already expensed in COGS, so amortization is ZERO
  // (no double count). Approximation: the whole installed base amortizes continuously.
  const hardwareAmortization = (!sold && L.hardwareAmortizationMonths > 0)
    ? hospitals * (hwPerHospital / L.hardwareAmortizationMonths)
    : 0;
  const ebitAfterHardware = ebitda - hardwareAmortization;

  // Cash flow for NPV/IRR: EBITDA minus the ACTUAL kiosk-hardware CAPEX cash spent
  // this month. In GIVEN mode capex = new hospitals × kiosks/hospital × kiosk cost.
  // In SOLD mode capex is ZERO — the kiosk cost is already inside COGS→EBITDA (and
  // offset by hardware revenue), so counting it again would double-count. CAC is
  // already inside EBITDA opex and the setup fee already inside EBITDA revenue.
  const capex = sold ? 0 : newH * hwPerHospital;
  const cashFlow = ebitda - capex;
  return {
    hospitals, newHospitals, consults,
    revenue, revenueSubscription, revenueUsage, revenueSetup, revenueOPD, revenueIPD,
    hardwareRevenue, hardwareCogs, mobileDataCost,
    ipdHospitals: ipd.ipdHospitals, ipdAdmissions: ipd.admissions, ipdBedDays: ipd.bedDays,
    aiCost, aiCostOPD, aiCostIPD, aiCostPerConsult: aiPerConsult,
    infra, cogs, grossMargin,
    grossMarginPct: revenue > 0 ? (grossMargin / revenue) * 100 : 0,
    staff: L.staffCostMonthlyINR, misc: L.miscOpexMonthlyINR, acquisition, opex,
    ebitda, hardwareAmortization, ebitAfterHardware,
    ebitdaMarginPct: revenue > 0 ? (ebitda / revenue) * 100 : 0,
    ebitMarginPct: revenue > 0 ? (ebitAfterHardware / revenue) * 100 : 0,
    capex, cashFlow,
  };
}

/** Monthly gross margin ONE (average) hospital contributes (excludes infra steps & opex). */
export function marginPerHospitalMonthly(L) {
  const opdConsults = L.consultsPerHospitalPerDay * L.workingDaysPerMonth;
  const aiPerConsult = effectiveAiCostPerConsult(L);
  // Recurring OPD revenue per hospital (the tiered model uses base + slabs; the
  // one-time setup fee is excluded from LTV/margin — it's not recurring).
  const opdRevenue = usesTieredRevenue(L)
    ? tieredMonthlyRevenuePerHospital(opdConsults, L)
    : subscriptionRevenuePerHospital(L)
      + (usesPerConsultRevenue(L) ? opdConsults * L.pricePerConsultINR : 0);
  const opdMargin = opdRevenue - opdConsults * aiPerConsult;
  // IPD margin for an AVERAGE hospital — ipdMonthly(1) is already prorated by the
  // % of hospitals that run IPD, so this is the blended per-hospital contribution.
  const ipd = ipdMonthly(1, L);
  const ipdMargin = ipd.revenue - ipd.aiCost;
  // Mobile-data allowance is a recurring per-hospital serving cost, so it belongs in
  // the recurring per-hospital margin (and therefore LTV). Hardware margin does NOT —
  // it's a one-time product sale, not recurring revenue.
  return opdMargin + ipdMargin - mobileDataPerHospital(L);
}

/** LTV, CAC ratio and payback from the levers. Churn 0 → LTV is unbounded (null). */
export function unitEconomics(L) {
  const margin = marginPerHospitalMonthly(L);
  const churn = Math.max(0, L.churnMonthlyPct) / 100;
  const ltv = churn > 0 ? margin / churn : null; // classic LTV = m / churn
  const cac = L.cacPerHospitalINR;
  // Payback must recover the FULL cost of landing a hospital: CAC + the kiosk
  // hardware handed over on day one (owner requirement — hardware is real money
  // even though EBITDA excludes its amortization).
  const hw = hardwarePerHospital(L);
  const landedCost = cac + hw;
  return {
    marginPerHospitalMonthly: margin,
    ltv,
    cac,
    hardwarePerHospital: hw,
    ltvToCac: ltv !== null && landedCost > 0 ? ltv / landedCost : null,
    paybackMonths: margin > 0 ? landedCost / margin : null,
  };
}

/**
 * Per-hospital economics as the installed base grows, h = 1 .. maxHospitals. Shows
 * WHY per-hospital cost falls at scale: the variable serving cost (AI incl. messaging
 * + mobile data) is constant per hospital, but the shared fixed cost (staff + misc)
 * and the tiered infra both AMORTIZE across more hospitals, so total cost/hospital
 * drops and margin/hospital expands. Revenue/hospital is the recurring subscription
 * (incl. tiered slabs) + the blended IPD contribution — constant per hospital. Pure.
 *
 * Each row: { h, servingCost, fixedShare, totalCostPerHospital, revenuePerHospital,
 *             marginPerHospital }. All figures are ₹/hospital/month.
 *  • servingCost = variable AI (STT+TTS+LLM+messaging, per aiPerConsult) + IPD AI +
 *                  mobile-data allowance  +  infra(h)/h  (the only serving term that
 *                  shrinks with h — the tiered "go server" cost spread over h hospitals).
 *  • fixedShare  = (staff + misc opex) / h  (pure fixed-cost amortization).
 *  Acquisition (CAC) and one-time hardware margin are one-time, excluded from this
 *  steady-state monthly view.
 */
export function perHospitalAtScale(L, maxHospitals) {
  const max = Math.max(1, Math.floor(maxHospitals || 1));
  const opdConsults = Math.max(0, (L.consultsPerHospitalPerDay || 0) * (L.workingDaysPerMonth || 0));
  const aiPerConsult = effectiveAiCostPerConsult(L);
  // Recurring OPD revenue per hospital (tiered = base + slabs; else subscription + hybrid usage).
  const opdRevenue = usesTieredRevenue(L)
    ? tieredMonthlyRevenuePerHospital(opdConsults, L)
    : subscriptionRevenuePerHospital(L)
      + (usesPerConsultRevenue(L) ? opdConsults * (L.pricePerConsultINR || 0) : 0);
  const ipd = ipdMonthly(1, L); // blended per-average-hospital IPD (prorated by ipdHospitalPct)
  const revenuePerHospital = opdRevenue + ipd.revenue;
  // The per-hospital VARIABLE serving cost — constant, independent of h.
  const variableServing = opdConsults * aiPerConsult // OPD AI incl. messaging
    + ipd.aiCost                                      // IPD AI (blended)
    + mobileDataPerHospital(L);                       // SIM/data allowance
  const fixedPool = Math.max(0, L.staffCostMonthlyINR || 0) + Math.max(0, L.miscOpexMonthlyINR || 0);
  const rows = [];
  for (let h = 1; h <= max; h += 1) {
    const infraShare = infraCostForHospitals(h, L) / h; // shrinks as h grows
    const servingCost = variableServing + infraShare;
    const fixedShare = fixedPool / h;
    const totalCostPerHospital = servingCost + fixedShare;
    rows.push({
      h,
      servingCost,
      fixedShare,
      totalCostPerHospital,
      revenuePerHospital,
      marginPerHospital: revenuePerHospital - totalCostPerHospital,
    });
  }
  return rows;
}

/**
 * Net present value of a monthly cash-flow series at an ANNUAL discount rate.
 * Each month's flow is discounted by the equivalent monthly rate
 * m = (1+annual)^(1/12) − 1: flow of month k (1-indexed) is divided by (1+m)^k, so
 * the first month's flow is already discounted one period (end-of-month convention).
 * Pure — pass the array of monthly cash flows and the annual rate in PERCENT.
 */
export function npv(cashFlows, annualRatePct) {
  const r = Math.max(0, annualRatePct || 0) / 100;
  const monthly = Math.pow(1 + r, 1 / 12) - 1;
  return cashFlows.reduce((s, cf, idx) => s + cf / Math.pow(1 + monthly, idx + 1), 0);
}

/**
 * Monthly internal rate of return of a cash-flow series, by bisection. The IRR is
 * the monthly rate that makes NPV zero. It only EXISTS when the series changes sign
 * (some money goes out before money comes in) — with no upfront outflow every flow
 * is positive and no rate zeroes the NPV, so we return null (the caller shows an
 * honest "n/a", never a fabricated number). Pure. Caveat: with multiple sign
 * changes IRR can be non-unique; bisection returns the single root in [-99.99%, +1000%/mo].
 */
export function monthlyIrr(cashFlows) {
  const hasNeg = cashFlows.some((c) => c < 0);
  const hasPos = cashFlows.some((c) => c > 0);
  if (!hasNeg || !hasPos) return null; // no sign change → IRR undefined
  const npvAt = (rate) => cashFlows.reduce((s, cf, idx) => s + cf / Math.pow(1 + rate, idx + 1), 0);
  let lo = -0.9999;   // rate can't go to −100% (division blows up)
  let hi = 10;        // 1000%/month upper bracket
  let flo = npvAt(lo);
  let fhi = npvAt(hi);
  if (flo * fhi > 0) return null; // no root bracketed → give up honestly
  for (let i = 0; i < 200; i += 1) {
    const mid = (lo + hi) / 2;
    const fmid = npvAt(mid);
    if (Math.abs(fmid) < 1e-7 || (hi - lo) < 1e-9) return mid;
    if (flo * fmid < 0) { hi = mid; fhi = fmid; } else { lo = mid; flo = fmid; }
  }
  return (lo + hi) / 2;
}

/** Annualized IRR (as a fraction, e.g. 0.42 = 42%/yr) from the monthly IRR, or null. */
export function annualizedIrr(cashFlows) {
  const m = monthlyIrr(cashFlows);
  if (m === null) return null;
  return Math.pow(1 + m, 12) - 1;
}

/**
 * Sensitivity: EBITDA at month `atMonth` (default: last) when one lever moves by
 * ±pct%. Returns [{lever, label, downEbitda, baseEbitda, upEbitda}] — pure. Only
 * numeric levers are perturbed; the AI-cost axis follows whichever knob is live
 * (the override number when override is on, else the LLM sub-lever), and the
 * pricing axes follow the active pricing model (tiered perturbs its base + slab-1).
 */
export function sensitivity(levers, opts = {}) {
  const pct = opts.pct ?? 20;
  const L = { ...DEFAULT_LEVERS, ...levers };
  const evalEbitda = (mod) => {
    const p = project({ ...L, ...mod });
    return p.months[p.months.length - 1]?.ebitda ?? 0;
  };
  const base = evalEbitda({});
  const aiAxis = L.aiCostOverrideEnabled
    ? ['aiCostOverrideINR', 'AI cost / consult']
    : ['llmPerConsultINR', 'LLM ₹ / consult'];
  // Pricing axes depend on the model so the strip always perturbs the live knobs.
  const priceAxis = L.pricingModel === 'tiered'
    ? ['slab1INR', 'Tier slab-1 ₹']
    : L.pricingModel === 'per-seat'
      ? ['pricePerSeatINR', 'Price / seat']
      : ['pricePerConsultINR', 'Price / consult'];
  const subAxis = L.pricingModel === 'tiered'
    ? ['tierBaseFeeINR', 'Tier base fee']
    : ['subscriptionPerHospitalINR', 'Subscription / hosp'];
  const axes = [
    priceAxis,
    aiAxis,
    subAxis,
    ['ipdPricePerAdmissionINR', 'IPD price / admit'],
  ];
  return axes.map(([lever, label]) => ({
    lever,
    label,
    baseEbitda: base,
    downEbitda: evalEbitda({ [lever]: L[lever] * (1 - pct / 100) }),
    upEbitda: evalEbitda({ [lever]: L[lever] * (1 + pct / 100) }),
    pct,
  }));
}

/**
 * N-month projection. Month 1 starts at startingHospitals (acquired before the
 * window, so no CAC charged for them); each subsequent month churns then adds.
 * Returns { months: [monthPnl + month index + cumulativeEbitda], summary }.
 */
export function project(levers) {
  const L = { ...DEFAULT_LEVERS, ...levers };
  const months = [];
  let hospitals = L.startingHospitals;
  let cumulativeEbitda = 0;
  let breakEvenMonth = null;
  for (let m = 1; m <= L.months; m += 1) {
    const newHospitals = m === 1 ? 0 : L.hospitalsAddedPerMonth;
    if (m > 1) {
      const churned = hospitals * (Math.max(0, L.churnMonthlyPct) / 100);
      hospitals = Math.max(0, hospitals - churned) + newHospitals;
    }
    const pnl = monthPnl(hospitals, newHospitals, L);
    cumulativeEbitda += pnl.ebitda;
    if (breakEvenMonth === null && pnl.ebitda >= 0) breakEvenMonth = m;
    months.push({ month: m, ...pnl, cumulativeEbitda });
  }
  const last = months[months.length - 1];
  const ue = unitEconomics(L);
  // Discounted-cash-flow finance metrics over the whole window.
  const cashFlows = months.map((x) => x.cashFlow);
  const npvINR = npv(cashFlows, L.discountRateAnnualPct);
  const irrFrac = annualizedIrr(cashFlows); // null when the series never changes sign
  return {
    months,
    summary: {
      ...ue,
      endingHospitals: last ? last.hospitals : L.startingHospitals,
      endingMonthlyRevenue: last ? last.revenue : 0,
      endingMonthlyRevenueOPD: last ? last.revenueOPD : 0,
      endingMonthlyRevenueIPD: last ? last.revenueIPD : 0,
      endingMonthlyEbitda: last ? last.ebitda : 0,
      aiCostPerConsult: effectiveAiCostPerConsult(L),
      cumulativeEbitda,
      breakEvenMonth, // first month with EBITDA ≥ 0, or null
      totalRevenue: months.reduce((s, x) => s + x.revenue, 0),
      totalRevenueOPD: months.reduce((s, x) => s + x.revenueOPD, 0),
      totalRevenueIPD: months.reduce((s, x) => s + x.revenueIPD, 0),
      totalAiCost: months.reduce((s, x) => s + x.aiCost, 0),
      totalAiCostIPD: months.reduce((s, x) => s + x.aiCostIPD, 0),
      totalInfra: months.reduce((s, x) => s + x.infra, 0),
      totalHardwareRevenue: months.reduce((s, x) => s + (x.hardwareRevenue || 0), 0),
      totalHardwareCogs: months.reduce((s, x) => s + (x.hardwareCogs || 0), 0),
      totalMobileData: months.reduce((s, x) => s + (x.mobileDataCost || 0), 0),
      hardwareMarginPerHospital: hardwareMarginPerHospital(L),
      hardwareSold: hardwareSold(L),
      // ── new (v3) cumulative + margin + DCF metrics ──
      totalEbitda: cumulativeEbitda,            // 12-month total EBITDA (== cumulativeEbitda)
      totalCapex: months.reduce((s, x) => s + x.capex, 0),
      grossMarginPctM12: last ? last.grossMarginPct : 0,
      ebitdaMarginPctM12: last ? last.ebitdaMarginPct : 0,
      ebitMarginPctM12: last ? last.ebitMarginPct : 0, // after-amortization (≈EBIT) margin
      discountRateAnnualPct: L.discountRateAnnualPct,
      npvINR,
      irrAnnualPct: irrFrac === null ? null : irrFrac * 100, // annualized IRR %, or null
    },
  };
}

/**
 * Derive a suggested blended AI-cost-per-consult from observed ops data: total
 * AI spend (INR) ÷ consult-ish runs. Both inputs are computed by the caller from
 * the live dataset; this stays pure. Returns null when there's nothing to derive.
 * (Fills the OVERRIDE — the single blended number — not the sub-levers.)
 */
export function deriveAiCostPerConsult(totalAiSpendINR, consultRuns) {
  if (!Number.isFinite(totalAiSpendINR) || !Number.isFinite(consultRuns) || consultRuns <= 0) return null;
  return totalAiSpendINR / consultRuns;
}
